/**
 * POINT EVENTS — "a double points weekend" (Taylor, 2026-11-03).
 *
 * A timed, site-wide multiplier an admin schedules. While it runs, every payout in its
 * scopes pays `multiplier` times. It never stacks with a boosted post: the larger of the
 * two applies, so a 3× post in a 2× weekend still pays 3×.
 *
 * Shared so the admin offers exactly the scopes the server accepts.
 */

export const POINT_EVENT_SCOPES = [
  'post-like',
  'post-comment',
  'comment-like',
  'critique-like',
  'critique-valued',
  'bounty',
] as const;

export type PointEventScope = (typeof POINT_EVENT_SCOPES)[number];

export const POINT_EVENT_SCOPE_LABEL: Readonly<Record<PointEventScope, string>> = Object.freeze({
  'post-like': 'Upvotes on posts',
  'post-comment': 'Comments on posts',
  'comment-like': 'Likes on comments',
  'critique-like': 'Upvotes on critiques',
  'critique-valued': 'Critiques marked valued',
  bounty: 'Bounty payouts',
});

export const POINT_EVENT_MULTIPLIER_MIN = 2;
export const POINT_EVENT_MULTIPLIER_MAX = 5;

/** The longest one event may run. A typo'd end date should not double points for a month. */
export const POINT_EVENT_MAX_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

export function isPointEventScope(value: string): value is PointEventScope {
  return (POINT_EVENT_SCOPES as readonly string[]).includes(value);
}

export type PointEventInput = {
  name: string;
  multiplier: number;
  scopes: readonly string[];
  startsAt: Date;
  endsAt: Date;
};

/** Why this event may not be scheduled, in an admin's words, or null when it may. */
export function pointEventViolation(input: PointEventInput, now: Date = new Date()): string | null {
  if (!input.name.trim()) return 'A points event needs a name.';
  if (
    !Number.isInteger(input.multiplier) ||
    input.multiplier < POINT_EVENT_MULTIPLIER_MIN ||
    input.multiplier > POINT_EVENT_MULTIPLIER_MAX
  ) {
    return `A multiplier is a whole number from ${POINT_EVENT_MULTIPLIER_MIN} to ${POINT_EVENT_MULTIPLIER_MAX}.`;
  }
  if (input.scopes.length === 0) return 'Pick at least one kind of payout it multiplies.';
  const unknown = input.scopes.find((scope) => !isPointEventScope(scope));
  if (unknown) return `"${unknown}" is not a payout a points event can multiply.`;
  const starts = input.startsAt.getTime();
  const ends = input.endsAt.getTime();
  if (Number.isNaN(starts) || Number.isNaN(ends)) return 'A points event needs a start and an end.';
  if (ends <= starts) return 'It has to end after it starts.';
  if (ends <= now.getTime()) return 'Its end has already passed.';
  if (ends - starts > POINT_EVENT_MAX_DAYS * DAY_MS) {
    return `A points event runs at most ${POINT_EVENT_MAX_DAYS} days.`;
  }
  return null;
}
